import React from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Text,
  Button,
  Input,
  Checkbox,
  Filter,
  Selector,
  NotificationBoxFooter,
  NotificationBox,
} from 'components'
import classNames from 'classnames'
import mask from 'utils/masks'
import { isMobile } from 'utils/deviceDetector'
import { validateEmail } from 'utils/validations'
import {
  setCustomerInfoLocalStorage,
  getCustomerInfoLocalStorage,
} from 'utils/customerInfoLocalStorage'
import styles from './AlertModal.module.scss'

const purposes = [
  { label: 'Quero comprar para morar', name: 'live' },
  { label: 'Quero comprar para investir', name: 'invest' },
]

const NotificationFooterProps = {
  renderTitle: () => {
    return (
      <Text color="primaryColor" fontSize="medium" bold>
        Edite os filtros para buscarmos por imóveis no perfil que você procura
      </Text>
    )
  },
}

const AlertModal = ({
  opened,
  closeModal,
  values,
  createAlert,
  setField,
  setFieldFilter,
  toggleFilter,
  isLoading,
  selectedFilter,
  clearFilters,
  removeSelectedLocality,
  setSelectedLocalities,
  setFieldManually,
  notificationBoxProps: { renderTitle, renderMessage },
  periodicityProps,
  confirmCheck,
  isDisabled,
  openFilterModal,
  openModal,
  setMaxValueRange,
  setMinValueRange,
  ...filter
}) => {
  const [expandFilters, setExpandFilters] = React.useState(false)
  const [touched, setTouched] = React.useState({})
  const [checkOffers] = React.useState(true)
  const [checkOffersResale] = React.useState(true)

  const handleBlur = field => () => setTouched({ ...touched, [field]: true })
  const { periodicity, purpose: statePurposes } = values

  const handleErrorMessage = (value, errorMessage) =>
    value === '' ? 'Campo obrigatório' : errorMessage

  const handleClose = () => {
    window.localStorage.setItem('disableAlertModal', true)
    setTouched({})
    closeModal()
  }

  const handleConfirm = valuesForm => () => {
    if (confirmCheck) {
      setTouched({ ...touched, email: true, submit: true, phone: true })
    } else {
      createAlert(valuesForm, checkOffers, checkOffersResale)
      window.localStorage.setItem('alertCreated', 'true')
      setCustomerInfoLocalStorage({
        ...getCustomerInfoLocalStorage(),
        phone: valuesForm.phone,
        email: valuesForm.email.trim(),
      })
      setTouched({})
    }
  }

  const onChange = e => {
    setTouched({ ...touched, [e.target.id]: false })
    setField(e.target.id, e.target.value)
  }

  const onChangeCheckbox = () => {
    setTouched({ ...touched, phone: false })
    setField('phone', '')
    setField('whatsapp', !values.whatsapp)
  }

  const onClickOptions = field => value => {
    if (field === 'periodicity') {
      const uncheckedOptions = Object.keys(periodicity).filter(
        key => key !== value
      )
      const forceUncheck = {}
      uncheckedOptions.forEach(option => {
        forceUncheck[option] = false
      })

      setField(field, { ...forceUncheck, [value]: !periodicity[value] })
    } else if (field === 'purpose') {
      setField(field, { ...statePurposes, [value]: !statePurposes[value] })
    }
    return null
  }

  const { renderTitle: renderFooterTitle } = NotificationFooterProps

  const specificFilters = !isMobile
    ? ['locality', 'surroundingsSearch', 'purpose', 'value', 'propertyType']
    : []

  return (
    <>
      <Modal
        opened={opened}
        closeModal={handleClose}
        className={styles.modalClass}
        noOverflowX
        closeButtonPosition="left"
      >
        {opened && (
          <section className={styles.alertModal}>
            <NotificationBox
              renderTitle={renderTitle}
              renderMessage={renderMessage}
            />

            <main className={styles.main}>
              <NotificationBoxFooter
                renderTitle={renderFooterTitle}
                showFilterButton
                onClick={
                  isMobile
                    ? openFilterModal
                    : () => setExpandFilters(!expandFilters)
                }
              />

              {expandFilters && (
                <section
                  className={classNames(styles.filtersContainer, {
                    [styles.expand]: expandFilters,
                  })}
                >
                  <hr className={styles.horizontalLine} />
                  <Filter
                    specificFilters={specificFilters}
                    className={styles.filterWrapper}
                    hideSearchById
                    type="alertModal"
                    titleContainer="Edição de filtros"
                    clearFilters={clearFilters}
                    setField={setFieldFilter}
                    toggleFilter={toggleFilter}
                    setSelectedLocalities={setSelectedLocalities}
                    removeSelectedLocality={removeSelectedLocality}
                    setFieldManually={setFieldManually}
                    setMaxValueRange={setMaxValueRange}
                    setMinValueRange={setMinValueRange}
                    {...filter}
                  />
                  <hr className={styles.horizontalLine} />
                </section>
              )}

              {periodicityProps && (
                <section className={styles.periodicitySection}>
                  <Text color="primaryColor" fontSize="medium" bold>
                    Com que frequência deseja receber as ofertas?
                  </Text>

                  {periodicityProps.options.map(option => (
                    <Checkbox
                      id={option.value}
                      option={option.label}
                      optionPosition="right-joined"
                      checked={option.checked}
                      onChange={() =>
                        onClickOptions('periodicity')(option.value)
                      }
                    />
                  ))}
                </section>
              )}

              <section className={styles.contactSection}>
                <Text color="primaryColor" fontSize="medium" bold>
                  Onde gostaria de receber as ofertas?
                </Text>
                <Input
                  errorMessage={handleErrorMessage(
                    values.email,
                    'Email inválido'
                  )}
                  onBlur={handleBlur('email')}
                  hasError={!validateEmail(values.email) && touched.email}
                  id="email"
                  type="email"
                  placeholder="email@email.com"
                  label={isMobile ? 'Seu e-mail*' : null}
                  value={values.email}
                  onChange={onChange}
                  labelTextColor="darkBlueColor"
                  className={styles.alertInput}
                />

                <section className={styles.whatsapp}>
                  <Checkbox
                    id={values.whatsapp.toString()}
                    option="Quero ser notificado também pelo Whatsapp"
                    optionPosition="right-joined"
                    onChange={onChangeCheckbox}
                    checked={values.whatsapp}
                  />
                </section>
                <section
                  className={classNames(styles.inputPhone, styles.alertInput, {
                    [styles.expand]: values.whatsapp,
                  })}
                >
                  <Input
                    errorMessage={handleErrorMessage(
                      values.phone,
                      'Celular inválido'
                    )}
                    onBlur={handleBlur('phone')}
                    hasError={
                      values.whatsapp &&
                      touched.phone &&
                      values.phone.replace(/[^0-9]/g, '').length !== 11
                    }
                    id="phone"
                    type="text"
                    label={isMobile ? 'Seu telefone celular' : null}
                    value={values.phone}
                    placeholder="(11) 99999-9999"
                    mask={mask.phone}
                    onChange={onChange}
                    labelTextColor="darkBlueColor"
                  />
                </section>
              </section>

              <section className={styles.purposeSection}>
                <Selector
                  tabbed
                  buttonClass={styles.purposeOption}
                  active={values.purpose}
                  onClick={onClickOptions('purpose')}
                  options={purposes}
                />
              </section>
            </main>

            <footer className={styles.footer}>
              <Text fontSize="xxsmall" color="lightBlueColor">
                Você poderá cancelar a assinatura a qualquer momento.
              </Text>
              {isMobile && <hr />}
              <Button
                id="submit"
                onClick={handleConfirm(values)}
                fontSize="medium"
                className={styles.submit}
                disabled={isLoading || isDisabled(checkOffersResale)}
                isLoading={isLoading}
                greenColor
              >
                Criar alerta
              </Button>
            </footer>
          </section>
        )}
      </Modal>
    </>
  )
}

AlertModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  values: PropTypes.shape({
    email: PropTypes.string.isRequired,
    periodicity: PropTypes.shape({}).isRequired,
    whatsapp: PropTypes.bool.isRequired,
    phone: PropTypes.string.isRequired,
    purpose: PropTypes.shape({
      live: PropTypes.bool.isRequired,
      invest: PropTypes.bool.isRequired,
    }),
  }).isRequired,
  createAlert: PropTypes.func.isRequired,
  setField: PropTypes.func.isRequired,
  setFieldFilter: PropTypes.func.isRequired,
  toggleFilter: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  selectedFilter: PropTypes.string.isRequired,
  clearFilters: PropTypes.func.isRequired,
  removeSelectedLocality: PropTypes.func.isRequired,
  setSelectedLocalities: PropTypes.func.isRequired,
  setFieldManually: PropTypes.func.isRequired,
  notificationBoxProps: PropTypes.shape({
    renderTitle: PropTypes.func.isRequired,
    renderMessage: PropTypes.func.isRequired,
  }).isRequired,
  periodicityProps: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        checked: PropTypes.bool,
      })
    ),
  }),
  confirmCheck: PropTypes.bool.isRequired,
  isDisabled: PropTypes.func.isRequired,
  openFilterModal: PropTypes.func.isRequired,
  openModal: PropTypes.bool.isRequired,
  setMaxValueRange: PropTypes.func.isRequired,
  setMinValueRange: PropTypes.func.isRequired,
}

AlertModal.defaultProps = {
  periodicityProps: () => {},
}

export default React.memo(AlertModal)
